import React, { useState, useEffect } from 'react';
import api from '../services/api';
import SearchableSelect from '../components/SearchableSelect';
import IconButton from '../components/IconButton';
import DateInput from '../components/DateInput';
import { useAuth } from '../context/AuthContext';

export default function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const { user } = useAuth();
  const hasPermission = (moduleName, action) => {
    const perms = user?.permissions || {};
    const key = Object.keys(perms).find(k => k.toLowerCase() === moduleName.toLowerCase());
    return !!(key && perms[key] && perms[key][action]);
  };

  // Helper flag for admin-only actions (backwards compatible with legacy `role` field)
  const isAdmin = ((user?.role_obj?.name) || user?.role || '').toLowerCase() === 'admin';

  const [roles, setRoles] = useState([]);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    dob: '',
    email: '',
    password: '',
    role_id: null,
    phone: ''
  });
  const [changePassword, setChangePassword] = useState(false);


  useEffect(() => {
    loadUsers();
    loadRoles();
  }, []);

  const loadUsers = async () => {
    try {
      const { data } = await api.get('/users');
      setUsers(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadRoles = async () => {
    try {
      const { data } = await api.get('/roles');
      setRoles(data || []);
    } catch (err) {
      console.error('Failed to load roles', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData };
      if (selectedUser) {
        // Edit existing user
        await api.put(`/users/${selectedUser.id}`, payload);
      } else {
        // Create new user
        await api.post('/users', payload);
      }
      loadUsers();
      handleCloseForm();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save user');
    }
  };

  const handleDeactivate = async (userId) => {
    try {
      await api.put(`/users/${userId}/deactivate`);
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to deactivate user');
    }
  };

  const handleActivate = async (userId) => {
    try {
      await api.put(`/users/${userId}/activate`);
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to activate user');
    }
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setFormData({
      firstName: user.firstName,
      lastName: user.lastName,
      dob: user?.dob?.split('T')[0], // Format date for input
      email: user.email,
      password: '', // Don't populate password for security
      role_id: user.role_id || null,
      joinedAt: user?.joinedAt?.split('T')[0],
      phone: user.phone || ''
    });
    setChangePassword(false);
    setShowForm(true);
  };

  const handleAddUser = () => {
    setSelectedUser(null);
    setFormData({
      firstName: '',
      lastName: '',
      dob: '',
      email: '',
      password: '',
      role: '',
      phone: '',
      joinedAt: ''
    });
    setChangePassword(false);
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setSelectedUser(null);
    setFormData({
      firstName: '',
      lastName: '',
      dob: '',
      email: '',
      password: '',
      role: 'user',
      phone: ''
    });
    setChangePassword(false);
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div style={{ color: 'red' }}>{error}</div>;

  return (
    <div className="users-container">
      <div className="header">
        <h2>Users</h2>
        {hasPermission('User', 'create') && (
          <button 
            onClick={handleAddUser}
            className="add-button add-action-button"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8 1V15M1 8H15" stroke="currentColor" strokeWidth="2"/>
            </svg>
            User
          </button>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      {showForm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>{selectedUser ? 'Edit User' : 'Add New User'}</h3>
              <button className="close-button" onClick={handleCloseForm}>&times;</button>
            </div>
            <form onSubmit={handleSubmit} className="user-form">
              <div className="form-group">
                <label>First Name:</label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label>Last Name:</label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label>Email:</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                />
              </div>

              {!selectedUser && (
                <div className="form-group">
                  <label>Password:</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    required={!selectedUser}
                    minLength="6"
                  />
                </div>
              )}

              {selectedUser && (
                <div className="form-group">
                  <label>
                    <input type="checkbox" checked={changePassword} onChange={(e) => setChangePassword(e.target.checked)} /> Change password
                  </label>
                </div>
              )}

              {selectedUser && changePassword && (
                <div className="form-group">
                  <label>New Password:</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    minLength="6"
                  />
                </div>
              )}

              <div className="form-group">
                <label>Phone:</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                />
              </div>

              <div className="form-group">
                <DateInput label="Date of Birth" value={formData.dob} onChange={val => setFormData({ ...formData, dob: val })} required />
              </div>
              <div className="form-group">
                <DateInput label="Date of Joining" value={formData.joinedAt} onChange={val => setFormData({ ...formData, joinedAt: val })} required />
              </div>

              {hasPermission('User', 'update') && (
                <div className="form-group">
                  <SearchableSelect
                    label="Role"
                    value={formData.role_id || ''}
                    options={roles.map((role) => ({ value: role.id, label: role.name }))}
                    onChange={(e) => setFormData({...formData, role_id: e.target.value ? Number(e.target.value) : null})}
                    placeholder="Select user role..."
                    required
                  />
                </div>
              )}

              <div className="form-actions">
                <button type="submit" className="submit-button">
                  {selectedUser ? 'Update User' : 'Create User'}
                </button>
                <button type="button" onClick={handleCloseForm} className="cancel-button">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="table-container table-responsive users-table">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Phone</th>
              <th>Date of Birth</th>
              <th>Status</th>
              <th>Joined</th>
              {isAdmin && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.id}>
                <td>{`${user.firstName} ${user.lastName}`}</td>
                <td>{user.email}</td>
                <td>{user.role_obj?.name || user.role}</td>
                <td>{user.phone || '-'}</td>
                <td>{new Date(user.dob).toLocaleDateString()}</td>
                <td>
                  <span className={`status-badge ${user.isActive ? 'active' : 'inactive'}`}>
                    {user.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>{new Date(user.joinedAt).toLocaleDateString()}</td>
                {isAdmin && (
                  <td>
                    <div className="action-buttons">
                      <IconButton title="Edit user" onClick={() => handleEdit(user)}>
                        <svg viewBox="0 0 24 24" fill="none"><path d="M3 21v-3.6l11-11L20.6 9 9.6 20H3z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      </IconButton>
                      {user.isActive ? (
                        <IconButton title="Deactivate user" onClick={() => handleDeactivate(user.id)}>
                          <svg viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><path d="M6 6l12 12" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                        </IconButton>
                      ) : (
                        <IconButton title="Activate user" onClick={() => handleActivate(user.id)}>
                          <svg viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                        </IconButton>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <style jsx>{`
        .users-container {
          padding: 20px;
          max-width: 1200px;
          margin: 0 auto;
        }

        .header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .add-button {
          background-color: #00875a;
          color: white;
          padding: 8px 16px;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-weight: 500;
        }

        .error-message {
          color: #de350b;
          background-color: #ffebe6;
          padding: 12px;
          border-radius: 4px;
          margin-bottom: 20px;
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
        }

        .modal {
          background: white;
          padding: 20px;
          border-radius: 8px;
          width: 90%;
          max-width: 500px;
          max-height: 90vh;
          overflow-y: auto;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .close-button {
          background: none;
          border: none;
          font-size: 24px;
          cursor: pointer;
          padding: 0;
          color: #42526e;
        }

        .user-form {
          display: flex;
          flex-direction: column;
          gap: 15px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 5px;
        }

        .form-group label {
          font-weight: 500;
        }

        .form-group input,
        .form-group select {
          padding: 8px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 14px;
        }

        .form-actions {
          display: flex;
          gap: 10px;
          margin-top: 20px;
        }

        .submit-button {
          background-color: #00875a;
          color: white;
          padding: 8px 16px;
          border: none;
          border-radius: 4px;
          cursor: pointer;
        }

        .cancel-button {
          background-color: #f4f5f7;
          color: #42526e;
          padding: 8px 16px;
          border: none;
          border-radius: 4px;
          cursor: pointer;
        }

      

        table {
          width: 100%;
          border-collapse: collapse;
        }

        th, td {
          padding: 12px 15px;
          text-align: left;
          border-bottom: 1px solid #ddd;
        }

        th {
          background-color: #f8f9fa;
          font-weight: 600;
        }

        .status-badge {
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 12px;
          text-transform: capitalize;
        }

        .status-badge.active {
          background-color: #e3fcef;
          color: #00875a;
        }

        .status-badge.inactive {
          background-color: #ffebe6;
          color: #de350b;
        }

        .action-buttons {
          display: flex;
          gap: 8px;
          align-items: center;
        }

        .action-buttons button {
          padding: 6px 12px;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 12px;
        }

        .action-buttons .icon-button {
          width: 34px;
          height: 34px;
          padding: 4px;
          border-radius: 6px;
          background: transparent;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .action-buttons .icon-button:hover {
          background: #f4f5f7;
        }

        .edit-button {
          background-color: #2684ff;
          color: white;
        }

        .deactivate-button {
          background-color: #de350b;
          color: white;
        }

        .activate-button {
          background-color: #00875a;
          color: white;
        }
      `}</style>
    </div>
  );
}